/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {gsap, TimelineLite} from "gsap";
import {DeadwoodRip} from "../../DeadwoodRip";
import {DeadwoodRipGameConfig} from "../../DeadwoodRipGameConfig";
import {IBetWayWinData} from "../../server/data/IDeadwoodRipData";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {DeadwoodRipReelController} from "../../reels/DeadwoodRipReelController";
import {ICountUp} from "../ICountUp";

export class IndividualWinController {

    private _countUp:ICountUp;
    private _currentTimeline:TimelineLite | null;

    private _stopped:boolean;
    private _winCap:boolean;
    private _allWinPositions:boolean[][];

    constructor() {
    }

    public start():void {
        this._stopped = false;
        const data = DeadwoodRip.model.parsedGameData;
        this._countUp = data.currentWinRatioConfig!.bigWinLevel > -1 ? DeadwoodRip.bigWinCountUp : DeadwoodRip.initialCountUp;
        this._winCap = data.brokeTheBank && data.betWins.some(w => w.multipliedAmount / data.playedBetValue >= DeadwoodRipGameConfig.WIN_CAP_RATIO);
        this.getAllWinPositions();
        DeadwoodRip.winView.startToggle();
        if(data.betWins.length > 0 && data.singleWin > 0 && !DeadwoodRip.isActionSpinsActive) {
            // Add a small delay for the first time, so we don't start to play before the autoPlay start next spin.
            this.playNextBetWin(0, 0.5);
        }
    }

    private getAllWinPositions():void {
        this._allWinPositions = ArrayHelper.initArrayWithValues<boolean[]>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
            return ArrayHelper.initArrayWithValues<boolean>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
        });
        (<IBetWayWinData[]>DeadwoodRip.model.parsedGameData.betWins).forEach(w => {
            w.reelWinPositions.forEach((reelWin, reelId) => {
                reelWin.forEach((symWin, symId) => {
                    if(symWin) {
                        this._allWinPositions[reelId][symId] = true;
                    }
                });
            });
        });
    }

    private playNextBetWin(index:number, delay:number = 0):void {
        if(this._stopped) {
            return;
        }

        const data = DeadwoodRip.model.parsedGameData;
        const winRatio = data.currentWinRatioConfig?.ratio!;
        let nextDelay = 0.8;
        const tl = new TimelineLite();
        if(index === data.betWins.length) {
            tl.add(this.getAllWinAnimation(winRatio), delay);
            nextDelay = 1.2;
        } else {
            if(index >= data.betWins.length) {
                index = 0;
            }
            if((index === 0 && data.betWins.length === 1) || this._winCap) {
                // do not play single if only one line or win cap.
            } else {
                tl.add(this.getBetWayAnimation(<IBetWayWinData>data.betWins[index], winRatio), delay);
            }
        }
        tl.add(() => {
            if(!this._stopped) {
                this.playNextBetWin(index + 1, nextDelay);
            }
        });
        this._currentTimeline = tl;
    }

    private getBetWayAnimation(winData:IBetWayWinData, winRatio:number):TimelineLite {
        const tl = new TimelineLite()
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 0, duration : 0.2}),
            gsap.to(DeadwoodRip.waysWon, {externalAlpha : 0, duration : 0.2})
        ], 0);
        const winTextPosition = DeadwoodRip.winView.getWinTextPosition(winData.reelWinPositions);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            if(reelWin[0] && (DeadwoodRip.model.parsedGameData.evaluatedArea[reelId][0] === "WXN" || reelId == 4)) {
                tl.add(this.getSymbolWinAnimation(reelId, 0, winRatio, reelId === winTextPosition.x, winData), 0);
            } else {
                reelWin.forEach((symWin, symId) => {
                    if(symWin) {
                        tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, reelId === winTextPosition.x && symId === winTextPosition.y, winData), 0);
                    }
                });
            }
            reelWin.forEach((winPosition, symId) => {
                if(winPosition) {
                    tl.add(DeadwoodRip.winView.getWinEffectAnimation(reelId, symId), 0);
                }
            });
        });
        return tl;
    }

    private getSymbolWinAnimation(reelId:number, symId:number, winRatio:number, showWinText:boolean, winData?:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();
        const sym = DeadwoodRip.reels[reelId].getSymbol(symId, false);
        tl.add([
            sym.playIndividualKill(),
            () => {
                if(!this._stopped && sym.transform) {
                    DeadwoodRip.reels[reelId].reelView.addWinSymbol(sym);
                }
                if(!this._stopped && showWinText && winData) {
                    DeadwoodRip.winView.playIndividualWinText(sym.getGlobalCenterPosition(), winData)
                }
            }]);

        return tl;
    }

    private getAllWinAnimation(winRatio:number):TimelineLite {
        const tl = new TimelineLite()
        tl.add([
            gsap.to(this._countUp, {externalAlpha : 1, duration : 0.2}),
            gsap.to(DeadwoodRip.waysWon, {externalAlpha : 1, duration : 0.2}),
            () => {
                this._countUp.showAfterRemoved();
                DeadwoodRip.waysWon.showAfterRemoved();
                DeadwoodRip.winView.removeToggleWinText();
            }
        ], 0);
        this._allWinPositions.forEach((reelWin, reelId) => {
            if(reelWin[0] && (DeadwoodRip.model.parsedGameData.evaluatedArea[reelId][0] === "WXN" || reelId == 4)) {
                tl.add(this.getSymbolWinAnimation(reelId, 0, winRatio, false), 0);
            } else {
                reelWin.forEach((symWin, symId) => {
                    if(symWin) {
                        tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio, false), 0);
                    }
                });
            }
            reelWin.forEach((winPosition, symId) => {
                if(winPosition) {
                    tl.add(DeadwoodRip.winView.getWinEffectAnimation(reelId, symId), 0);
                }
            });
        });
        return tl;
    }

    public async stop():Promise<any> {
        this._stopped = true;
        if(this._currentTimeline && this._currentTimeline.isActive()) {
            await this._currentTimeline.progress(1);
            this._currentTimeline.kill();
        }
        this._currentTimeline = null;
        DeadwoodRip.winView.stopToggle();
        await Promise.resolve();
    }
}